import { AuthoringArtifactSchema } from '@dl/shared';
import { z } from 'zod';

/**
 * Wire messages for LLM-led interactive authoring (ADR-0008 §6). Three
 * concerns, three transports:
 *
 *   - **Live turn → SSE** (worker → initiating client): token deltas, tool
 *     "thinking" events, the provisional artifact diff, the assistant's
 *     conversational reply, and a turn-done marker. Never touches the DO.
 *   - **Durable session → the DO scratch store** (resume on open) — not a wire
 *     concern here.
 *   - **Committed result → the existing op-log WS broadcast** — the `define_*`
 *     ops broadcast for free (see `sync.ts`); commit is the request/response
 *     below, but the resulting ops ride the ordinary op-push path.
 *
 * The turn request opens the SSE stream; the commit request/response is a plain
 * HTTP call the GM makes to ratify the artifact (ADR-0008 §5 — commit is a
 * direct, auditor-gated GM action; there is no commit tool in the agent loop).
 *
 * All shapes are JSON-serialisable — SSE frames are JSON payloads, and the
 * request/response bodies persist through the client Model.
 */

// ── turn request (opens the SSE stream) ─────────────────────────────────────

/**
 * Client → worker: run one refine turn against a session. `direction` is the
 * GM's steering text for this turn ("add a `severity` enum", "make it apply to
 * Factions too", or an opening "I want to model corruption"). The worker runs
 * inference, streams the {@link AuthoringServerEvent} union back over SSE, and
 * persists `{turn, artifact snapshot, tool trace}` to the DO after the turn
 * (ADR-0008 §2).
 */
export const AuthoringTurnRequestSchema = z
  .object({
    session_id: z.string().min(1),
    direction: z.string().min(1),
  })
  .strict();
export type AuthoringTurnRequest = z.infer<typeof AuthoringTurnRequestSchema>;

// ── SSE server events ───────────────────────────────────────────────────────

/** SSE: a streamed token delta of the assistant's output. */
export const AuthoringTokenEventSchema = z
  .object({
    kind: z.literal('token'),
    text: z.string(),
  })
  .strict();
export type AuthoringTokenEvent = z.infer<typeof AuthoringTokenEventSchema>;

/**
 * SSE: a tool "thinking" event as the agent loop grounds itself (ADR-0008 §6)
 * — a read tool (`search_entities`, `list_predicates`, …) or a stage-only
 * `propose_*` tool. `status` tracks the tool's lifecycle; `detail` is optional
 * human-readable context (never user-rendered raw — the UI maps `name`/`status`
 * to its own i18n string).
 */
export const AuthoringToolEventSchema = z
  .object({
    kind: z.literal('tool'),
    name: z.string().min(1),
    status: z.enum(['started', 'succeeded', 'failed']),
    detail: z.string().optional(),
  })
  .strict();
export type AuthoringToolEvent = z.infer<typeof AuthoringToolEventSchema>;

/**
 * SSE: the provisional artifact diff (ADR-0008 §1/§6) — emitted whenever a
 * `propose_*` tool materialises or patches the session artifact. Carries the
 * full resolved {@link AuthoringArtifactSchema} (which embeds its own `diff`),
 * so the surface can render the live diff + commit affordance without a
 * follow-up fetch.
 */
export const AuthoringArtifactDiffEventSchema = z
  .object({
    kind: z.literal('artifact_diff'),
    artifact: AuthoringArtifactSchema,
  })
  .strict();
export type AuthoringArtifactDiffEvent = z.infer<typeof AuthoringArtifactDiffEventSchema>;

/**
 * SSE: the assistant's conversational reply for an exploratory turn (ADR-0008
 * §1 — a turn with no concrete op proposal yet gets a clarify/sketch reply).
 * The full assembled message (the token stream is the incremental form).
 */
export const AuthoringAssistantMessageEventSchema = z
  .object({
    kind: z.literal('assistant_message'),
    text: z.string(),
  })
  .strict();
export type AuthoringAssistantMessageEvent = z.infer<
  typeof AuthoringAssistantMessageEventSchema
>;

/**
 * SSE: the turn is complete. `turn_id` identifies this turn (matches the
 * artifact's `provenance.turn_id`). `cost` surfaces the **actual** metered
 * gateway cost of the turn (ADR-0008 §7/§8 — actual per-turn cost only, no
 * pre-run estimate); optional because a turn may end before any metered call.
 */
export const AuthoringTurnDoneEventSchema = z
  .object({
    kind: z.literal('turn_done'),
    turn_id: z.string().min(1),
    cost: z
      .object({ usd: z.number().nonnegative() })
      .strict()
      .optional(),
  })
  .strict();
export type AuthoringTurnDoneEvent = z.infer<typeof AuthoringTurnDoneEventSchema>;

/**
 * SSE: a turn-fatal error (budget wall hit mid-turn, provider error,
 * capability-unsupported, …). `code` is a stable machine-readable tag the UI
 * maps to an i18n string; `message` is diagnostic, never user-rendered raw.
 */
export const AuthoringErrorEventSchema = z
  .object({
    kind: z.literal('error'),
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .strict();
export type AuthoringErrorEvent = z.infer<typeof AuthoringErrorEventSchema>;

/**
 * The SSE event union streamed during a live authoring turn (ADR-0008 §6):
 * `token | tool | artifact_diff | assistant_message | turn_done | error`.
 * Discriminated on `kind`, matching the `sync.ts` envelope convention.
 */
export const AuthoringServerEventSchema = z.discriminatedUnion('kind', [
  AuthoringTokenEventSchema,
  AuthoringToolEventSchema,
  AuthoringArtifactDiffEventSchema,
  AuthoringAssistantMessageEventSchema,
  AuthoringTurnDoneEventSchema,
  AuthoringErrorEventSchema,
]);
export type AuthoringServerEvent = z.infer<typeof AuthoringServerEventSchema>;

// ── commit request / response ───────────────────────────────────────────────

/**
 * Client → worker/DO: ratify the session's current artifact (ADR-0008 §5).
 * Commit is the GM's explicit action — the LLM never commits. The DO runs the
 * auditor in pack-import mode, then (if not blocked) appends the `define_*`
 * op(s) in one atomic transaction.
 */
export const AuthoringCommitRequestSchema = z
  .object({
    session_id: z.string().min(1),
  })
  .strict();
export type AuthoringCommitRequest = z.infer<typeof AuthoringCommitRequestSchema>;

/**
 * One auditor advisory raised by a commit (ADR-0008 §5, `§5.2` pack-import
 * mode). A `blocking_error` refuses the commit (fix-or-cancel);
 * `warning` / `info` surface for acknowledgment but do not block.
 */
export const AuthoringAdvisorySchema = z
  .object({
    severity: z.enum(['blocking_error', 'warning', 'info']),
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .strict();
export type AuthoringAdvisory = z.infer<typeof AuthoringAdvisorySchema>;

/**
 * The ids the commit minted + appended (ADR-0008 §5 — commit links the op ids
 * ↔ session for provenance). `op_ids` are the appended `define_*` op ids;
 * `trait_id` / `predicate_ids` are the server-minted opaque vocab ids for the
 * committed trait + its new member predicates.
 */
export const AuthoringCommittedSchema = z
  .object({
    op_ids: z.array(z.string().min(1)).readonly(),
    trait_id: z.string().min(1).optional(),
    predicate_ids: z.array(z.string().min(1)).readonly().optional(),
  })
  .strict();
export type AuthoringCommitted = z.infer<typeof AuthoringCommittedSchema>;

/**
 * Worker/DO → client: the commit outcome. `ok: true` ⇒ the artifact committed
 * and `committed` carries the minted ids. `ok: false` ⇒ a `blocking_error`
 * advisory refused it (see `advisories`), nothing was appended. `advisories`
 * may also be present on success (surfaced `warning` / `info`).
 */
export const AuthoringCommitResponseSchema = z
  .object({
    ok: z.boolean(),
    committed: AuthoringCommittedSchema.optional(),
    advisories: z.array(AuthoringAdvisorySchema).readonly().optional(),
  })
  .strict();
export type AuthoringCommitResponse = z.infer<typeof AuthoringCommitResponseSchema>;
